import { useEffect, useMemo, useState } from 'react'
import api from '@/lib/api'
import { Card, CardContent } from '@/components/ui/Card'
import { SearchInput } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { DataGrid, type DataGridColumn } from '@/components/ui/DataGrid'
import { useUsers } from '@/hooks/useUsers'
import { useAuthStore } from '@/stores/authStore'
import { QUESTION_GROUPS as PRIVACY_GROUPS } from '@/pages/AssessmentPrivacyPage'
import {
  ClipboardCheck, ExternalLink, Eye, TrendingUp, Users as UsersIcon,
  RefreshCw, CheckCircle2, XCircle, UserPlus, X, Building2, Mail, Phone, BadgeCheck,
  Database, Server, ShieldCheck, Target, Check, FileSpreadsheet,
  Pencil, History, ChevronDown, Globe, Save, AlertCircle, Gauge,
} from 'lucide-react'

type SubmissionItem = {
  id: string
  assessment_slug: string
  assessment_name: string
  pillar: string
  status: string
  contact_name: string
  contact_email: string
  contact_phone?: string | null
  contact_role?: string | null
  company_name: string
  company_cnpj?: string | null
  company_size?: string | null
  company_sector?: string | null
  total_score: number
  total_percentage: number
  maturity_band?: string | null
  lead_id?: string | null
  company_id?: string | null
  contact_id?: string | null
  submitted_at?: string | null
  created_at: string
}

type ListResponse = {
  total: number
  page: number
  page_size: number
  items: SubmissionItem[]
}

type Stats = {
  total_submissions: number
  by_pillar: Record<string, number>
  by_band: Record<string, number>
  by_status: Record<string, number>
  last_30_days: number
}

type CRMRecord = {
  id?: string | null
  name?: string | null
  email?: string | null
  cnpj?: string | null
  status?: string | null
  owner_id?: string | null
  owner_name?: string | null
  exists: boolean
}

type CRMInfo = {
  submission_id: string
  company: CRMRecord
  contact: CRMRecord
  lead: CRMRecord
  all_registered: boolean
}

const PILLAR_LABEL: Record<string, string> = {
  iga: 'IGA',
  privacy: 'Privacidade',
  value_chain: 'Cadeia de Valor',
  sustainability: 'Sustentabilidade',
  cybersecurity: 'Cibersegurança',
  cybersec: 'Cibersegurança',
}

async function downloadXLSX(submissionId: string, companyName: string) {
  try {
    const res = await api.get(`/v1/assessments-admin/submissions/${submissionId}/export-xlsx`, {
      responseType: 'blob',
    })
    const blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })
    const safeCompany = (companyName || 'empresa').replace(/[^a-zA-Z0-9-_]/g, '_').slice(0, 40)
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `BS4IT_Assessment_${safeCompany}_${submissionId.slice(0, 8)}.xlsx`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  } catch (err) {
    console.error('Failed to download XLSX:', err)
    alert('Erro ao gerar XLSX. Veja o console para detalhes.')
  }
}

const BAND_COLOR: Record<string, string> = {
  initial: 'bg-red-100 text-red-800 border-red-300',
  basic: 'bg-orange-100 text-orange-800 border-orange-300',
  intermediate: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  advanced: 'bg-blue-100 text-blue-800 border-blue-300',
  optimized: 'bg-emerald-100 text-emerald-800 border-emerald-300',
}

// Rota pública do resultado por slug (o slug nem sempre coincide com a rota)
const PUBLIC_RESULT_PATH: Record<string, string> = {
  'iga-maturity': 'iga',
  'privacy-maturity': 'privacy',
  'value-chain-integrity': 'cadeia-de-valor',
}

const BAND_LABEL: Record<string, string> = {
  initial: 'Inicial',
  basic: 'Básico',
  intermediate: 'Intermediário',
  advanced: 'Avançado',
  optimized: 'Otimizado',
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  submitted: 'Submetido',
  qualified: 'Qualificado',
  archived: 'Arquivado',
}

function formatDate(iso?: string | null) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
  } catch {
    return iso
  }
}

function StatCard({ icon: Icon, label, value }: { icon: any; label: string; value: string | number }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
            <p className="text-2xl font-bold mt-0.5">{value}</p>
          </div>
          <Icon className="h-7 w-7 text-emerald-500/40" />
        </div>
      </CardContent>
    </Card>
  )
}

export default function AssessmentsAdminPage() {
  const [items, setItems] = useState<SubmissionItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize] = useState(100)
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState<Stats | null>(null)

  const [pillarFilter, setPillarFilter] = useState<string>('')
  const [bandFilter, setBandFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [search, setSearch] = useState('')

  const [viewSubmission, setViewSubmission] = useState<SubmissionItem | null>(null)

  async function loadList() {
    setLoading(true)
    try {
      const params: Record<string, string | number> = { page, page_size: pageSize }
      if (pillarFilter) params.pillar = pillarFilter
      if (bandFilter) params.band = bandFilter
      if (statusFilter) params.status = statusFilter
      if (search.trim()) params.search = search.trim()
      const { data } = await api.get<ListResponse>('/v1/assessments-admin/submissions', { params })
      setItems(data.items)
      setTotal(data.total)
    } catch (err) {
      console.error('Failed to load submissions:', err)
    } finally {
      setLoading(false)
    }
  }

  async function loadStats() {
    try {
      const { data } = await api.get<Stats>('/v1/assessments-admin/stats')
      setStats(data)
    } catch (err) {
      console.error('Failed to load stats:', err)
    }
  }

  useEffect(() => { loadStats() }, [])
  useEffect(() => {
    const t = setTimeout(loadList, 200)
    return () => clearTimeout(t)
  }, [page, pillarFilter, bandFilter, statusFilter, search])

  const columns = useMemo<DataGridColumn<SubmissionItem>[]>(() => [
    {
      id: 'when', header: 'Quando', minWidth: '140px',
      accessor: (s) => <span className="text-xs text-muted-foreground">{formatDate(s.submitted_at || s.created_at)}</span>,
      sortValue: (s) => new Date(s.submitted_at || s.created_at).getTime(),
      filterValue: (s) => formatDate(s.submitted_at || s.created_at),
    },
    {
      id: 'pillar', header: 'Pilar',
      accessor: (s) => <Badge variant="outline">{PILLAR_LABEL[s.pillar] || s.pillar}</Badge>,
      sortValue: (s) => s.pillar,
      filterValue: (s) => PILLAR_LABEL[s.pillar] || s.pillar,
    },
    {
      id: 'contact', header: 'Contato',
      accessor: (s) => (
        <div>
          <button type="button" onClick={(e) => { e.stopPropagation(); setViewSubmission(s) }}
            className="font-medium text-primary hover:underline text-left">
            {s.contact_name}
          </button>
          <div className="text-xs text-muted-foreground">{s.contact_email}</div>
          {s.contact_role && <div className="text-xs text-muted-foreground italic">{s.contact_role}</div>}
        </div>
      ),
      sortValue: (s) => s.contact_name,
      filterValue: (s) => `${s.contact_name} ${s.contact_email} ${s.contact_role || ''}`,
    },
    {
      id: 'company', header: 'Empresa',
      accessor: (s) => (
        <div>
          <div className="font-medium">{s.company_name}</div>
          {s.company_cnpj && <div className="text-xs text-muted-foreground font-mono">{s.company_cnpj}</div>}
          {s.company_sector && <div className="text-xs text-muted-foreground">{s.company_sector}</div>}
        </div>
      ),
      sortValue: (s) => s.company_name,
      filterValue: (s) => `${s.company_name} ${s.company_cnpj || ''} ${s.company_sector || ''}`,
    },
    {
      id: 'score', header: 'Score', align: 'center' as const,
      accessor: (s) => (
        <div className="text-center">
          <div className="font-bold">{Math.round(s.total_percentage)}%</div>
          <div className="text-[10px] text-muted-foreground">{s.total_score} pts</div>
        </div>
      ),
      sortValue: (s) => s.total_percentage,
      filterValue: (s) => `${Math.round(s.total_percentage)}%`,
    },
    {
      id: 'maturity', header: 'Maturidade',
      accessor: (s) => s.maturity_band ? (
        <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold border ${BAND_COLOR[s.maturity_band] || ''}`}>
          {BAND_LABEL[s.maturity_band] || s.maturity_band}
        </span>
      ) : <span className="text-xs text-muted-foreground">—</span>,
      sortValue: (s) => s.maturity_band || '',
      filterValue: (s) => BAND_LABEL[s.maturity_band || ''] || '',
    },
    {
      id: 'status', header: 'Status',
      accessor: (s) => <Badge variant="secondary">{STATUS_LABEL[s.status] || s.status}</Badge>,
      sortValue: (s) => s.status,
      filterValue: (s) => STATUS_LABEL[s.status] || s.status,
    },
    {
      id: 'crm', header: 'CRM',
      accessor: (s) => (
        s.lead_id ? (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" /> Cadastrado
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-amber-700">
            <XCircle className="h-3.5 w-3.5" /> Pendente
          </span>
        )
      ),
      sortValue: (s) => s.lead_id ? 1 : 0,
      filterValue: (s) => s.lead_id ? 'cadastrado' : 'pendente',
    },
    {
      id: 'actions', header: 'Ações', sortable: false, filterable: false, align: 'right' as const,
      accessor: (s) => (
        <div className="flex justify-end gap-1" onClick={(e: any) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" onClick={() => setViewSubmission(s)} title="Ver lead">
            <Eye className="h-4 w-4" />
          </Button>
          {s.assessment_slug === 'bs4it-cybersec' && (
            <Button variant="ghost" size="icon"
              onClick={() => downloadXLSX(s.id, s.company_name)}
              title="Exportar XLSX (formato BS4IT)"
              className="text-emerald-600 hover:text-emerald-700">
              <FileSpreadsheet className="h-4 w-4" />
            </Button>
          )}
          {s.assessment_slug !== 'bs4it-cybersec' && (
            <a
              href={`https://social.myncc.com.br/assessment/${PUBLIC_RESULT_PATH[s.assessment_slug] || s.assessment_slug.replace('-maturity', '')}/resultado/${s.id}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-accent text-muted-foreground"
              title="Abrir resultado público"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
      ),
    },
  ], [])

  return (
    <div className="min-h-screen">
      <div className="p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ClipboardCheck className="h-6 w-6 text-emerald-600" />
              Assessments
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Submissões dos diagnósticos públicos do Nai Compliance Center
            </p>
          </div>
          <Button variant="outline" onClick={() => { loadList(); loadStats() }} disabled={loading}
            leftIcon={<RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />}>
            Atualizar
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard icon={UsersIcon} label="Total" value={stats.total_submissions} />
            <StatCard icon={TrendingUp} label="Últimos 30 dias" value={stats.last_30_days} />
            <StatCard icon={BadgeCheck} label="IGA" value={stats.by_pillar.iga || 0} />
            <StatCard icon={ClipboardCheck} label="Submetidos" value={stats.by_status.submitted || 0} />
            <StatCard icon={CheckCircle2} label="Avançado/Otimizado"
              value={(stats.by_band.advanced || 0) + (stats.by_band.optimized || 0)} />
            <StatCard icon={XCircle} label="Inicial/Básico"
              value={(stats.by_band.initial || 0) + (stats.by_band.basic || 0)} />
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex flex-1 gap-3 flex-wrap">
            <div className="w-64">
              <SearchInput placeholder="Buscar contato/empresa/CNPJ..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                onClear={() => setSearch('')} />
            </div>
            <select className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={pillarFilter} onChange={(e) => { setPillarFilter(e.target.value); setPage(1) }}>
              <option value="">Todos os pilares</option>
              <option value="iga">IGA</option>
              <option value="privacy">Privacidade</option>
              <option value="value_chain">Cadeia de Valor</option>
              <option value="sustainability">Sustentabilidade</option>
              <option value="cybersec">Cibersegurança</option>
            </select>
            <select className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={bandFilter} onChange={(e) => { setBandFilter(e.target.value); setPage(1) }}>
              <option value="">Todas as maturidades</option>
              <option value="initial">Inicial</option>
              <option value="basic">Básico</option>
              <option value="intermediate">Intermediário</option>
              <option value="advanced">Avançado</option>
              <option value="optimized">Otimizado</option>
            </select>
            <select className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}>
              <option value="">Todos os status</option>
              <option value="draft">Rascunho</option>
              <option value="submitted">Submetido</option>
              <option value="qualified">Qualificado</option>
              <option value="archived">Arquivado</option>
            </select>
          </div>
        </div>

        {/* Grid */}
        <Card>
          <CardContent className="p-0">
            <DataGrid<SubmissionItem>
              gridId="assessments-admin"
              columns={columns}
              data={items}
              isLoading={loading}
              emptyMessage="Nenhuma submissão encontrada"
              emptyIcon={<ClipboardCheck className="h-12 w-12 opacity-30" />}
              getRowKey={(s) => s.id}
              renderExpandedRow={(s) => <SubmissionDetailPanel submissionId={s.id} pillar={s.pillar} slug={s.assessment_slug} />}
            />
          </CardContent>
        </Card>
      </div>

      {viewSubmission && (
        <SubmissionDetailModal
          submission={viewSubmission}
          onClose={() => setViewSubmission(null)}
          onSynced={() => { loadList(); loadStats() }}
        />
      )}
    </div>
  )
}

// ─── Inline expansion (accordion) ───────────────────────────────────────────────

type LegacyFieldDef = {
  id: string
  label: string
  type: string
  options?: string[]
  depends_on?: string
}
type LegacySectionDef = { id: string; title: string; subtitle?: string; fields: LegacyFieldDef[] }
type AssessmentDefinition = {
  slug: string
  legacy_sections: LegacySectionDef[]
  domains?: unknown[]
}
type SubmissionDetail = {
  id: string
  legacy_systems: Record<string, any>
  answers: Record<string, any>
  domain_scores: Record<string, any>
  recommendations: any[]
  ip_address?: string | null
  user_agent?: string | null
  utm_source?: string | null
  utm_campaign?: string | null
  referrer?: string | null
  started_at?: string | null
  submitted_at?: string | null
  created_at?: string | null
  contact_name?: string | null
  contact_email?: string | null
  contact_phone?: string | null
  contact_role?: string | null
  company_name?: string | null
}

type EditLogEntry = {
  id: string
  section_id?: string | null
  field_id: string
  field_label?: string | null
  old_value: any
  new_value: any
  action: string
  note?: string | null
  user_id?: string | null
  user_name?: string | null
  user_email?: string | null
  created_at: string
}

// Cache de definitions (uma por slug) — evita refetch a cada expansão
const definitionCache: Record<string, AssessmentDefinition> = {}

async function fetchDefinition(slug: string): Promise<AssessmentDefinition | null> {
  if (definitionCache[slug]) return definitionCache[slug]
  try {
    const { data } = await api.get<AssessmentDefinition>(`/v1/assessments/${slug}/definition`)
    definitionCache[slug] = data
    return data
  } catch {
    return null
  }
}

function formatLegacyValue(v: any): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'Sim' : 'Não'
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—'
  return String(v)
}

// Ícones por seção
const SECTION_ICON: Record<string, any> = {
  directories: Database,
  targets: Server,
  infra: ShieldCheck,
  project: Target,
  // Cadeia de Valor
  cadeia: Building2,
  exposicao: ShieldCheck,
  ferramentas: Server,
  projeto: Target,
}

function FieldValue({ field, value, otherText }: {
  field: LegacyFieldDef
  value: any
  otherText?: string | null
}) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground italic">não informado</span>
  }
  if (typeof value === 'boolean') {
    return (
      <span className={`inline-flex items-center gap-1 font-semibold ${
        value ? 'text-emerald-700' : 'text-muted-foreground'
      }`}>
        {value ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
        {value ? 'Sim' : 'Não'}
      </span>
    )
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-muted-foreground italic">vazio</span>
    return (
      <div className="flex flex-wrap gap-1">
        {value.map((v, i) => (
          <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0">{v}</Badge>
        ))}
        {otherText && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
            Outro: {otherText}
          </Badge>
        )}
      </div>
    )
  }
  if (field.type === 'number') {
    return <span className="font-mono font-semibold">{value}</span>
  }
  return (
    <span>
      {String(value)}
      {otherText && (
        <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0">
          Outro: {otherText}
        </Badge>
      )}
    </span>
  )
}

// BS4IT Detail Panel — formato customizado por causa da estrutura aninhada
function BS4ITDetailPanel({ data, submissionId }: {
  data: { dados_cadastrais?: any; servidores?: any[]; ativos?: any[]; informacoes?: Record<string, any> }
  submissionId: string
}) {
  const dados = data.dados_cadastrais || {}
  const servidores = data.servidores || []
  const ativos = data.ativos || []
  const info = data.informacoes || {}

  const DADOS_FIELDS: Array<[string, string]> = [
    ['Razão Social', 'razao_social'],
    ['CNPJ', 'cnpj'],
    ['Inscrição Estadual', 'inscricao_estadual'],
    ['Endereço', 'endereco'],
    ['Sponsor', 'sponsor_nome'],
    ['E-mail', 'sponsor_email'],
    ['Telefone fixo', 'telefone_fixo'],
    ['Celular', 'celular'],
  ]

  const INFO_LABELS: Array<[string, string]> = [
    ['q_colaboradores', 'Colaboradores'],
    ['q_usuarios_ti', 'Usuários TI'],
    ['q_filiais', 'Filiais/unidades'],
    ['q_operacoes_internacionais', 'Operações internacionais'],
    ['q_ambiente', 'Ambiente (On-prem/Cloud/Híbrido)'],
    ['q_clouds', 'Clouds utilizadas'],
    ['q_endpoints', 'Endpoints'],
    ['q_servidores', 'Servidores'],
    ['q_dispositivos_rede', 'Dispositivos de rede'],
    ['q_firewalls', 'Firewalls'],
    ['q_ot_ics', 'OT/ICS'],
    ['q_containers_k8s', 'Containers/K8s'],
    ['q_apps_criticas_internet', 'Apps críticas na internet'],
    ['q_sistemas_criticos', 'Sistemas críticos'],
    ['q_classificacao_ativos', 'Classificação de ativos'],
    ['q_cmdb', 'CMDB'],
    ['q_ambientes_segregados', 'Ambientes segregados'],
    ['q_equipe_seguranca', 'Equipe interna de segurança'],
    ['q_soc_atual', 'SOC atual'],
    ['q_maturidade_atual', 'Maturidade atual'],
    ['q_processo_incidentes', 'Processo de incidentes'],
    ['q_playbook', 'Playbook'],
    ['q_gestao_vulnerabilidades', 'Gestão de vulnerabilidades'],
    ['q_awareness', 'Awareness'],
    ['q_framework', 'Framework'],
    ['q_siem', 'SIEM'],
    ['q_volume_logs', 'Volume de logs'],
    ['q_retencao', 'Retenção'],
    ['q_requisito_retencao', 'Requisito regulatório'],
    ['q_integracoes', 'Integrações obrigatórias'],
    ['q_modelo_soc', 'Modelo SOC'],
    ['q_chamados_mes', 'Chamados/mês'],
    ['q_incidentes_mes', 'Incidentes/mês'],
  ]

  return (
    <div className="bg-muted/20 px-6 py-5 border-l-4 border-primary/60">
      <div className="flex items-center gap-2 mb-4">
        <ClipboardCheck className="h-5 w-5 text-primary" />
        <h3 className="text-base font-semibold">Dados coletados · BS4IT Cibersegurança</h3>
        <Badge variant="secondary" className="ml-auto">4 blocos · {servidores.length} servidores · {ativos.length} ativos</Badge>
        <Button variant="outline" size="sm"
          onClick={(e: any) => {
            e.stopPropagation()
            downloadXLSX(submissionId, dados.razao_social || 'empresa')
          }}
          className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
          leftIcon={<FileSpreadsheet className="h-4 w-4" />}>
          Exportar XLSX
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Dados cadastrais */}
        <section className="rounded-lg border bg-card p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <h3 className="text-base font-semibold">Dados cadastrais</h3>
          </div>
          <dl className="space-y-2.5 pt-1">
            {DADOS_FIELDS.map(([label, key]) => (
              <div key={key} className="text-sm grid grid-cols-[1fr_1.6fr] gap-3 items-start">
                <dt className="text-[11px] text-muted-foreground font-medium pt-0.5">{label}</dt>
                <dd className="text-foreground break-words">
                  {dados[key] || <span className="text-muted-foreground italic">não informado</span>}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Servidores */}
        <section className="rounded-lg border bg-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Server className="h-5 w-5 text-primary" />
              <h3 className="text-base font-semibold">Servidores</h3>
            </div>
            <Badge variant="outline" className="text-[10px]">{servidores.length} registros</Badge>
          </div>
          {servidores.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Nenhum servidor cadastrado.</p>
          ) : (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                  <tr>
                    <th className="text-left px-2 py-1.5">Unid.</th>
                    <th className="text-left px-2 py-1.5">SO</th>
                    <th className="text-left px-2 py-1.5">Hostname</th>
                    <th className="text-left px-2 py-1.5">Amb.</th>
                    <th className="text-left px-2 py-1.5">Prioridade</th>
                    <th className="text-left px-2 py-1.5">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {servidores.map((s: any, i: number) => (
                    <tr key={i}>
                      <td className="px-2 py-1.5">{s.unid || '—'}</td>
                      <td className="px-2 py-1.5">{s.so || '—'}</td>
                      <td className="px-2 py-1.5 font-mono">{s.hostname || '—'}</td>
                      <td className="px-2 py-1.5">{s.ambiente || '—'}</td>
                      <td className="px-2 py-1.5">{s.prioridade || '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{s.ip || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Ativos SegInfo */}
        <section className="rounded-lg border bg-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              <h3 className="text-base font-semibold">Ativos de SegInfo</h3>
            </div>
            <Badge variant="outline" className="text-[10px]">{ativos.length} registros</Badge>
          </div>
          {ativos.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Nenhum ativo cadastrado.</p>
          ) : (
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                  <tr>
                    <th className="text-left px-2 py-1.5">Unid.</th>
                    <th className="text-left px-2 py-1.5">Tipo</th>
                    <th className="text-left px-2 py-1.5">Hostname</th>
                    <th className="text-left px-2 py-1.5">Prioridade</th>
                    <th className="text-left px-2 py-1.5">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {ativos.map((a: any, i: number) => (
                    <tr key={i}>
                      <td className="px-2 py-1.5">{a.unid || '—'}</td>
                      <td className="px-2 py-1.5">{a.tipo || '—'}</td>
                      <td className="px-2 py-1.5 font-mono">{a.hostname || '—'}</td>
                      <td className="px-2 py-1.5">{a.prioridade || '—'}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{a.ip || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Informações complementares */}
        <section className="rounded-lg border bg-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              <h3 className="text-base font-semibold">Informações complementares</h3>
            </div>
            <Badge variant="outline" className="text-[10px]">
              {Object.values(info).filter(v => v && String(v).trim()).length}/{INFO_LABELS.length}
            </Badge>
          </div>
          <dl className="space-y-2 pt-1 max-h-[400px] overflow-y-auto pr-2">
            {INFO_LABELS.map(([key, label]) => {
              const v = info[key]
              return (
                <div key={key} className="text-sm grid grid-cols-[1fr_1.4fr] gap-3 items-start">
                  <dt className="text-[11px] text-muted-foreground font-medium pt-0.5">{label}</dt>
                  <dd className="text-foreground break-words">
                    {v ? String(v) : <span className="text-muted-foreground italic">—</span>}
                  </dd>
                </div>
              )
            })}
          </dl>
        </section>
      </div>
    </div>
  )
}

// ─── Consents & Audit components ────────────────────────────────────────────────

function ConsentRow({ label, granted, required }: {
  label: string; granted: boolean; required?: boolean
}) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className={`mt-0.5 inline-flex items-center justify-center h-5 w-5 rounded-full ${
        granted
          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
          : 'bg-muted text-muted-foreground'
      }`}>
        {granted ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      </span>
      <div className="flex-1">
        <div className="font-medium">{label}</div>
        {required && <div className="text-[10px] text-muted-foreground">obrigatório (LGPD art. 7º, I)</div>}
      </div>
      <span className={`text-[10px] font-semibold ${granted ? 'text-emerald-700' : 'text-muted-foreground'}`}>
        {granted ? 'ACEITO' : 'NÃO ACEITO'}
      </span>
    </div>
  )
}

function ConsentsCard({ consents, detail, fallbackIp, fallbackUA }: {
  consents: Record<string, any>
  detail: SubmissionDetail
  fallbackIp?: string | null
  fallbackUA?: string | null
}) {
  const ip = consents.ip || fallbackIp || null
  const ua = consents.user_agent || fallbackUA || null
  const ts = consents.timestamp || detail.submitted_at || detail.created_at || null

  const respondentLines: Array<[string, string]> = []
  if (detail.contact_name) respondentLines.push(['Nome', detail.contact_name])
  if (detail.contact_email) respondentLines.push(['E-mail', detail.contact_email])
  if (detail.contact_role) respondentLines.push(['Cargo', detail.contact_role])
  if (detail.contact_phone) respondentLines.push(['Telefone', detail.contact_phone])
  if (detail.company_name) respondentLines.push(['Empresa', detail.company_name])

  return (
    <section className="rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50/40 dark:bg-emerald-950/20 p-4 space-y-3 lg:col-span-2">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-emerald-700" />
        <h3 className="text-base font-semibold text-emerald-900 dark:text-emerald-200">
          Consentimentos e identificação do respondente
        </h3>
        <Badge variant="outline" className="ml-auto text-[10px] border-emerald-300 text-emerald-800">
          Prova LGPD
        </Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-md border bg-card p-3 space-y-2.5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Aceites registrados
          </div>
          <ConsentRow
            label="Envio do relatório por e-mail"
            granted={Boolean(consents.relatorio)}
            required
          />
          <ConsentRow
            label="Contato comercial pelo time NAI"
            granted={Boolean(consents.contato)}
          />
          <ConsentRow
            label="Recebimento de newsletter e conteúdos"
            granted={Boolean(consents.newsletter)}
          />
        </div>

        <div className="rounded-md border bg-card p-3 space-y-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Identificação do respondente
          </div>
          <dl className="space-y-1.5 text-sm">
            {respondentLines.map(([label, value]) => (
              <div key={label} className="grid grid-cols-[80px_1fr] gap-2 items-start">
                <dt className="text-[11px] text-muted-foreground font-medium pt-0.5">{label}</dt>
                <dd className="break-words">{value}</dd>
              </div>
            ))}
          </dl>

          <div className="border-t pt-2 space-y-1.5 text-sm">
            <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
              <dt className="text-[11px] text-muted-foreground font-medium pt-0.5 flex items-center gap-1">
                <Globe className="h-3 w-3" /> IP
              </dt>
              <dd className="font-mono text-xs">{ip || <span className="text-muted-foreground italic">não capturado</span>}</dd>
            </div>
            <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
              <dt className="text-[11px] text-muted-foreground font-medium pt-0.5">Aceite em</dt>
              <dd className="text-xs">{ts ? formatDate(ts) : <span className="text-muted-foreground italic">—</span>}</dd>
            </div>
            {ua && (
              <div className="grid grid-cols-[80px_1fr] gap-2 items-start">
                <dt className="text-[11px] text-muted-foreground font-medium pt-0.5">User-Agent</dt>
                <dd className="text-[11px] font-mono break-all leading-snug">{ua}</dd>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

function formatLogValue(v: any): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'boolean') return v ? 'Sim' : 'Não'
  if (Array.isArray(v)) return v.length ? v.join(', ') : '(vazio)'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

function EditLogAccordion({ logs }: { logs: EditLogEntry[] }) {
  if (logs.length === 0) return null
  return (
    <details className="mt-4 rounded-lg border bg-card">
      <summary className="cursor-pointer p-3 flex items-center gap-2 select-none hover:bg-accent/30 rounded-lg">
        <History className="h-4 w-4 text-muted-foreground" />
        <span className="text-xs font-semibold uppercase tracking-wider">
          Histórico de alterações
        </span>
        <Badge variant="secondary" className="text-[10px]">{logs.length}</Badge>
        <ChevronDown className="h-4 w-4 text-muted-foreground ml-auto" />
      </summary>
      <div className="border-t">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30">
            <tr>
              <th className="text-left px-3 py-2">Quando</th>
              <th className="text-left px-3 py-2">Quem</th>
              <th className="text-left px-3 py-2">Campo</th>
              <th className="text-left px-3 py-2">De</th>
              <th className="text-left px-3 py-2">Para</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {logs.map((l) => (
              <tr key={l.id} className="hover:bg-accent/20">
                <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                  {formatDate(l.created_at)}
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{l.user_name || l.user_email || '—'}</div>
                  {l.user_email && l.user_name && (
                    <div className="text-[10px] text-muted-foreground">{l.user_email}</div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{l.field_label || l.field_id}</div>
                  {l.section_id && (
                    <div className="text-[10px] text-muted-foreground font-mono">{l.section_id}</div>
                  )}
                </td>
                <td className="px-3 py-2 max-w-[200px] break-words">
                  <span className="line-through text-muted-foreground">{formatLogValue(l.old_value)}</span>
                </td>
                <td className="px-3 py-2 max-w-[200px] break-words">
                  <span className="font-medium text-emerald-700 dark:text-emerald-400">
                    {formatLogValue(l.new_value)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  )
}

// ─── Edit modal ────────────────────────────────────────────────────────────────

function EditSectionModal({
  submissionId, section, legacy, onClose, onSaved,
}: {
  submissionId: string
  section: LegacySectionDef
  legacy: Record<string, any>
  onClose: () => void
  onSaved: () => Promise<void> | void
}) {
  const initial = useMemo(() => {
    const o: Record<string, any> = {}
    section.fields.forEach(f => { o[f.id] = legacy[f.id] ?? null })
    return o
  }, [section, legacy])

  const [values, setValues] = useState<Record<string, any>>(initial)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  function setVal(id: string, v: any) {
    setValues(prev => ({ ...prev, [id]: v }))
  }

  function normalize(v: any): any {
    if (v === '' || v === undefined) return null
    return v
  }

  async function save() {
    setSaving(true); setErr(null)
    try {
      const changes = section.fields.filter(f => {
        const oldV = legacy[f.id]
        const newV = normalize(values[f.id])
        const a = Array.isArray(oldV) ? JSON.stringify(oldV) : (oldV ?? null)
        const b = Array.isArray(newV) ? JSON.stringify(newV) : newV
        return a !== b
      })
      for (const f of changes) {
        await api.patch(`/v1/assessments-admin/submissions/${submissionId}/legacy-field`, {
          section_id: section.id,
          field_id: f.id,
          field_label: f.label,
          value: normalize(values[f.id]),
        })
      }
      await onSaved()
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Erro ao salvar alterações')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-card border rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-base font-bold">Editar — {section.title}</h2>
            {section.subtitle && (
              <p className="text-xs text-muted-foreground mt-0.5">{section.subtitle}</p>
            )}
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Fechar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {section.fields.map((f) => (
            <EditField key={f.id} field={f} value={values[f.id]} onChange={(v) => setVal(f.id, v)} />
          ))}
        </div>

        {err && (
          <div className="px-4 py-2 border-t bg-destructive/10 text-destructive text-xs flex items-center gap-2">
            <AlertCircle className="h-4 w-4" /> {err}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 p-3 border-t bg-muted/30">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button size="sm" onClick={save} disabled={saving}
            leftIcon={saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}>
            {saving ? 'Salvando…' : 'Salvar alterações'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function EditField({ field, value, onChange }: {
  field: LegacyFieldDef; value: any; onChange: (v: any) => void
}) {
  const t = field.type
  const opts = field.options || []
  if (t === 'boolean' || t === 'yes_no') {
    return (
      <div className="grid grid-cols-[1fr_2fr] gap-3 items-center">
        <label className="text-xs text-muted-foreground">{field.label}</label>
        <div className="flex gap-2">
          {[['Sim', true], ['Não', false], ['—', null]].map(([lbl, v]) => (
            <button key={String(lbl)} type="button" onClick={() => onChange(v)}
              className={`px-3 py-1 rounded-md text-xs border ${
                value === v
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background hover:bg-accent'
              }`}>
              {lbl as string}
            </button>
          ))}
        </div>
      </div>
    )
  }
  if (t === 'select' || t === 'radio') {
    return (
      <div className="grid grid-cols-[1fr_2fr] gap-3 items-center">
        <label className="text-xs text-muted-foreground">{field.label}</label>
        <select
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value || null)}>
          <option value="">— não informado —</option>
          {opts.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    )
  }
  if (t === 'multiselect' || t === 'checkboxes') {
    const arr: string[] = Array.isArray(value) ? value : []
    function toggle(o: string) {
      onChange(arr.includes(o) ? arr.filter(x => x !== o) : [...arr, o])
    }
    return (
      <div className="grid grid-cols-[1fr_2fr] gap-3 items-start">
        <label className="text-xs text-muted-foreground pt-1">{field.label}</label>
        <div className="flex flex-wrap gap-1.5">
          {opts.map(o => (
            <button key={o} type="button" onClick={() => toggle(o)}
              className={`px-2.5 py-1 rounded-md text-xs border ${
                arr.includes(o)
                  ? 'bg-primary/10 text-primary border-primary'
                  : 'bg-background hover:bg-accent'
              }`}>
              {o}
            </button>
          ))}
        </div>
      </div>
    )
  }
  if (t === 'number') {
    return (
      <div className="grid grid-cols-[1fr_2fr] gap-3 items-center">
        <label className="text-xs text-muted-foreground">{field.label}</label>
        <input type="number"
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />
      </div>
    )
  }
  if (t === 'textarea') {
    return (
      <div className="grid grid-cols-[1fr_2fr] gap-3 items-start">
        <label className="text-xs text-muted-foreground pt-1">{field.label}</label>
        <textarea rows={3}
          className="rounded-md border border-input bg-background px-2 py-1 text-sm"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)} />
      </div>
    )
  }
  return (
    <div className="grid grid-cols-[1fr_2fr] gap-3 items-center">
      <label className="text-xs text-muted-foreground">{field.label}</label>
      <input type="text"
        className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)} />
    </div>
  )
}

// Privacidade — dados_cadastrais + informacoes (5 blocos) + scoring, no mesmo layout do IGA
const PRIVACY_DADOS_FIELDS: Array<[string, string]> = [
  ['razao_social', 'Razão Social'],
  ['cnpj', 'CNPJ'],
  ['inscricao_estadual', 'Inscrição Estadual'],
  ['endereco', 'Endereço'],
  ['sponsor_nome', 'Sponsor'],
  ['sponsor_email', 'E-mail'],
  ['telefone_fixo', 'Telefone fixo'],
  ['celular', 'Celular / WhatsApp'],
  ['cargo', 'Cargo'],
  ['setor', 'Setor'],
  ['porte', 'Porte (colaboradores)'],
  ['urgencia', 'Horizonte para implantar'],
]

function isFilled(v: any): boolean {
  if (v === null || v === undefined || v === '') return false
  if (Array.isArray(v) && v.length === 0) return false
  return true
}

function PrivacyDetailPanel({ legacy, detail, logs }: {
  legacy: Record<string, any>
  detail: SubmissionDetail
  logs: EditLogEntry[]
}) {
  const dados = legacy.dados_cadastrais || {}
  const info = legacy.informacoes || {}
  const scoring = legacy.scoring || {}
  const pilares: Record<string, any> = scoring.pilares || {}
  const banda = scoring.banda || null

  const dadosFilled = PRIVACY_DADOS_FIELDS.filter(([k]) => isFilled(dados[k])).length

  return (
    <div className="bg-muted/20 px-6 py-5 border-l-4 border-primary/60">
      <div className="flex items-center gap-2 mb-4">
        <ClipboardCheck className="h-5 w-5 text-primary" />
        <h3 className="text-base font-semibold">Respostas informadas · Nai Data Privacy</h3>
        <Badge variant="secondary" className="ml-auto">
          {PRIVACY_GROUPS.length} blocos · dados cadastrais
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Resultado */}
        <section className="rounded-lg border bg-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Gauge className="h-5 w-5 text-primary" />
              <h3 className="text-base font-semibold">Resultado</h3>
            </div>
            {banda && (
              <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold border"
                style={{ borderColor: banda.color, color: banda.color }}>
                {banda.label}
              </span>
            )}
          </div>
          {banda?.tom && <p className="text-xs text-muted-foreground -mt-1">{banda.tom}</p>}
          <dl className="space-y-2.5 pt-1">
            <div className="text-sm grid grid-cols-[1fr_1.6fr] gap-3 items-start">
              <dt className="text-[11px] text-muted-foreground font-medium pt-0.5">Score total</dt>
              <dd className="font-mono font-semibold">
                {scoring.score_total ?? detail.total_percentage ?? '—'}
                <span className="text-muted-foreground font-normal"> / 100</span>
              </dd>
            </div>
            {Object.entries(pilares).map(([key, p]) => (
              <div key={key} className="text-sm grid grid-cols-[1fr_1.6fr] gap-3 items-start">
                <dt className="text-[11px] text-muted-foreground font-medium pt-0.5">{p.label || key}</dt>
                <dd>
                  <span className="font-mono font-semibold">{p.normalizado ?? '—'}</span>
                  <span className="text-muted-foreground"> / {p.peso ?? '—'}</span>
                  {typeof p.gap === 'number' && (
                    <span className="ml-2 text-[11px] text-muted-foreground">gap {p.gap}/{p.max_gap}</span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Dados cadastrais */}
        <section className="rounded-lg border bg-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              <h3 className="text-base font-semibold">Dados cadastrais</h3>
            </div>
            <Badge variant={dadosFilled === PRIVACY_DADOS_FIELDS.length ? 'default' : 'outline'} className="text-[10px]">
              {dadosFilled}/{PRIVACY_DADOS_FIELDS.length} preenchidos
            </Badge>
          </div>
          <dl className="space-y-2.5 pt-1">
            {PRIVACY_DADOS_FIELDS.map(([key, label]) => (
              <div key={key} className="text-sm grid grid-cols-[1fr_1.6fr] gap-3 items-start">
                <dt className="text-[11px] text-muted-foreground font-medium pt-0.5">{label}</dt>
                <dd className="text-foreground break-words">
                  {isFilled(dados[key]) ? String(dados[key]) : <span className="text-muted-foreground italic">não informado</span>}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        {/* 5 blocos de perguntas */}
        {PRIVACY_GROUPS.map((group) => {
          const Icon = group.icon || ClipboardCheck
          const filled = group.questions.filter(q => isFilled(info[q.id])).length
          return (
            <section key={group.id} className="rounded-lg border bg-card p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className="h-5 w-5 text-primary" />
                  <h3 className="text-base font-semibold">{group.title}</h3>
                </div>
                <Badge variant={filled === group.questions.length ? 'default' : 'outline'} className="text-[10px]">
                  {filled}/{group.questions.length} preenchidos
                </Badge>
              </div>
              {group.subtitle && <p className="text-xs text-muted-foreground -mt-1">{group.subtitle}</p>}
              <dl className="space-y-2.5 pt-1">
                {group.questions.map((q) => (
                  <div key={q.id} className="text-sm grid grid-cols-[1fr_1.6fr] gap-3 items-start">
                    <dt className="text-[11px] text-muted-foreground font-medium pt-0.5">{q.label}</dt>
                    <dd className="text-foreground break-words">
                      <FieldValue
                        field={{ id: q.id, label: q.label, type: q.type === 'multi' ? 'multi_select' : 'single_select' }}
                        value={info[q.id]}
                        otherText={info[`${q.id}_other`] || null}
                      />
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          )
        })}

        <ConsentsCard
          consents={legacy.consents || {}}
          detail={detail}
          fallbackIp={detail.ip_address}
          fallbackUA={detail.user_agent}
        />
      </div>

      <EditLogAccordion logs={logs} />
    </div>
  )
}

function SubmissionDetailPanel({ submissionId, pillar, slug }: { submissionId: string; pillar: string; slug: string }) {
  const [detail, setDetail] = useState<SubmissionDetail | null>(null)
  const [defn, setDefn] = useState<AssessmentDefinition | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editSection, setEditSection] = useState<LegacySectionDef | null>(null)
  const [logs, setLogs] = useState<EditLogEntry[]>([])

  const isBS4IT = pillar === 'cybersec'
  // Privacidade grava dados_cadastrais/informacoes/scoring — sem definition no registry
  const isPrivacy = pillar === 'privacy'
  const usesDefinition = !isBS4IT && !isPrivacy

  async function loadLogs() {
    try {
      const { data } = await api.get<EditLogEntry[]>(`/v1/assessments-admin/submissions/${submissionId}/edit-logs`)
      setLogs(data)
    } catch (err) {
      console.warn('Falha ao carregar logs', err)
    }
  }

  async function reload() {
    try {
      const { data } = await api.get<SubmissionDetail>(`/v1/assessments-admin/submissions/${submissionId}`)
      setDetail(data)
      await loadLogs()
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erro ao recarregar detalhes')
    }
  }

  useEffect(() => {
    let cancel = false
    setLoading(true)
    setError(null)
    const promises: Promise<any>[] = [
      api.get<SubmissionDetail>(`/v1/assessments-admin/submissions/${submissionId}`),
      api.get<EditLogEntry[]>(`/v1/assessments-admin/submissions/${submissionId}/edit-logs`),
    ]
    if (usesDefinition) promises.push(fetchDefinition(slug))
    Promise.all(promises)
      .then((res) => {
        if (cancel) return
        setDetail(res[0].data)
        setLogs(res[1].data || [])
        if (usesDefinition && res[2]) setDefn(res[2])
      })
      .catch((err) => { if (!cancel) setError(err?.response?.data?.detail || 'Erro ao carregar detalhes') })
      .finally(() => { if (!cancel) setLoading(false) })
    return () => { cancel = true }
  }, [submissionId, pillar, slug, usesDefinition])

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Carregando contexto técnico…</div>
  }
  if (error) {
    return <div className="p-6 text-sm text-destructive">{error}</div>
  }
  if (!detail) return null

  const legacy = detail.legacy_systems || {}

  // BS4IT: estrutura aninhada com dados_cadastrais/servidores/ativos/informacoes
  if (isBS4IT) {
    return <BS4ITDetailPanel data={legacy as any} submissionId={submissionId} />
  }

  if (isPrivacy) {
    return <PrivacyDetailPanel legacy={legacy} detail={detail} logs={logs} />
  }

  const sections = defn?.legacy_sections || []

  const knownFieldIds = new Set<string>()
  sections.forEach(s => s.fields.forEach(f => {
    knownFieldIds.add(f.id)
    knownFieldIds.add(`${f.id}_other`)
  }))
  const unknownEntries = Object.entries(legacy)
    .filter(([k, v]) => !knownFieldIds.has(k) && v !== null && v !== undefined && v !== '')

  return (
    <div className="bg-muted/20 px-6 py-5 border-l-4 border-primary/60">
      <div className="flex items-center gap-2 mb-4">
        <ClipboardCheck className="h-5 w-5 text-primary" />
        <h3 className="text-base font-semibold">
          {pillar === 'iga' ? 'Contexto técnico informado' : 'Contexto da operação informado'}
        </h3>
        <Badge variant="secondary" className="ml-auto">
          {pillar === 'iga'
            ? 'Etapa 11 · Sistemas e dimensão do projeto'
            : `Etapa ${(defn?.domains?.length || 0) + 1} · Perfil, exposição e contexto do projeto`}
        </Badge>
      </div>

      {sections.length === 0 ? (
        <div className="text-sm text-muted-foreground">Definição não disponível.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {sections.map((section) => {
            const Icon = SECTION_ICON[section.id] || ClipboardCheck
            const visibleFields = section.fields.filter((f) => {
              if (!f.depends_on) return true
              return Boolean(legacy[f.depends_on])
            })
            const filledCount = visibleFields.filter(f => {
              const v = legacy[f.id]
              if (v === null || v === undefined || v === '') return false
              if (Array.isArray(v) && v.length === 0) return false
              return true
            }).length
            const isComplete = filledCount === visibleFields.length && visibleFields.length > 0

            return (
              <section key={section.id} className="rounded-lg border bg-card p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5 text-primary" />
                    <h3 className="text-base font-semibold">{section.title}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={isComplete ? 'default' : 'outline'} className="text-[10px]">
                      {filledCount}/{visibleFields.length} preenchidos
                    </Badge>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setEditSection(section) }}
                      title="Editar campos desta seção"
                      className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {section.subtitle && (
                  <p className="text-xs text-muted-foreground -mt-1">{section.subtitle}</p>
                )}

                <dl className="space-y-2.5 pt-1">
                  {visibleFields.map((field) => {
                    const v = legacy[field.id]
                    const otherText = legacy[`${field.id}_other`] || null
                    return (
                      <div key={field.id} className="text-sm grid grid-cols-[1fr_1.6fr] gap-3 items-start">
                        <dt className="text-[11px] text-muted-foreground font-medium pt-0.5">
                          {field.label}
                        </dt>
                        <dd className="text-foreground break-words">
                          <FieldValue field={field} value={v} otherText={otherText} />
                        </dd>
                      </div>
                    )
                  })}
                </dl>
              </section>
            )
          })}

          <ConsentsCard
            consents={legacy.consents || {}}
            detail={detail}
            fallbackIp={detail.ip_address}
            fallbackUA={detail.user_agent}
          />

          {unknownEntries.length > 0 && (
            <details className="rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/20 lg:col-span-2">
              <summary className="cursor-pointer p-4 flex items-center gap-2 select-none">
                <ClipboardCheck className="h-4 w-4 text-amber-700" />
                <span className="text-xs font-semibold text-amber-900 dark:text-amber-200 uppercase tracking-wider">
                  Campos legados ({unknownEntries.length})
                </span>
                <span className="ml-auto text-[10px] text-amber-700">
                  Versões antigas do questionário
                </span>
              </summary>
              <dl className="space-y-2 text-sm px-4 pb-4">
                {unknownEntries
                  .filter(([k]) => k !== 'consents')
                  .map(([k, v]) => (
                    <div key={k} className="grid grid-cols-[1fr_1.6fr] gap-3">
                      <dt className="text-[11px] font-mono text-amber-700 pt-0.5">{k}</dt>
                      <dd>{formatLegacyValue(v)}</dd>
                    </div>
                  ))}
              </dl>
            </details>
          )}
        </div>
      )}

      <EditLogAccordion logs={logs} />

      {editSection && detail && (
        <EditSectionModal
          submissionId={submissionId}
          section={editSection}
          legacy={legacy}
          onClose={() => setEditSection(null)}
          onSaved={async () => { setEditSection(null); await reload() }}
        />
      )}
    </div>
  )
}


// ─── Modal ──────────────────────────────────────────────────────────────────────

function SubmissionDetailModal({
  submission, onClose, onSynced,
}: { submission: SubmissionItem; onClose: () => void; onSynced: () => void }) {
  const [crm, setCrm] = useState<CRMInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<string | null>(null)

  const currentUser = useAuthStore((s) => s.user)
  const { data: usersData } = useUsers({ perPage: 100 })
  const ownerOptions = useMemo(() => (usersData?.items || [])
    .filter((u: any) => u.is_active !== false)
    .map((u: any) => ({ value: u.id, label: u.full_name || u.email || u.id })),
    [usersData?.items])

  const [ownerId, setOwnerId] = useState<string>('')

  async function loadCrm() {
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get<CRMInfo>(`/v1/assessments-admin/submissions/${submission.id}/crm-info`)
      setCrm(data)
      // Default owner: lead's current owner, else current user
      setOwnerId(data.lead.owner_id || currentUser?.id || '')
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erro ao carregar info CRM')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadCrm() }, [submission.id])

  async function handleSync() {
    setSyncing(true)
    setError(null)
    setFeedback(null)
    try {
      const { data } = await api.post(`/v1/assessments-admin/submissions/${submission.id}/sync-crm`,
        { owner_id: ownerId || null })
      setFeedback(data.created ? 'Lead, empresa e contato cadastrados no CRM ✓' : 'Responsável atualizado ✓')
      await loadCrm()
      onSynced()
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erro ao sincronizar com CRM')
    } finally {
      setSyncing(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-card border rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline">{PILLAR_LABEL[submission.pillar] || submission.pillar}</Badge>
              {submission.maturity_band && (
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold border ${BAND_COLOR[submission.maturity_band] || ''}`}>
                  {BAND_LABEL[submission.maturity_band] || submission.maturity_band}
                </span>
              )}
            </div>
            <h2 className="text-lg font-bold">{submission.company_name}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Submetido em {formatDate(submission.submitted_at || submission.created_at)} ·
              Score {Math.round(submission.total_percentage)}% ({submission.total_score} pts)
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Fechar">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {/* Contato (reduzido) */}
          <section className="rounded-lg border bg-muted/30 p-4 space-y-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Contato</div>
            <div className="font-semibold">{submission.contact_name}</div>
            <div className="text-sm space-y-1">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Mail className="h-3.5 w-3.5" /> {submission.contact_email}
              </div>
              {submission.contact_phone && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone className="h-3.5 w-3.5" /> {submission.contact_phone}
                </div>
              )}
              {submission.contact_role && (
                <div className="text-xs text-muted-foreground italic">{submission.contact_role}</div>
              )}
            </div>
          </section>

          {/* Empresa (reduzido) */}
          <section className="rounded-lg border bg-muted/30 p-4 space-y-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Empresa</div>
            <div className="font-semibold flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              {submission.company_name}
            </div>
            <div className="text-sm text-muted-foreground space-y-0.5">
              {submission.company_cnpj && <div className="font-mono text-xs">{submission.company_cnpj}</div>}
              <div className="flex gap-3 text-xs">
                {submission.company_sector && <span>{submission.company_sector}</span>}
                {submission.company_size && <span>· {submission.company_size}</span>}
              </div>
            </div>
          </section>

          {/* CRM status */}
          <section className="rounded-lg border p-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3">Status no CRM</div>
            {loading ? (
              <p className="text-sm text-muted-foreground">Carregando…</p>
            ) : crm ? (
              <>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <CRMStatusItem label="Empresa" record={crm.company} />
                  <CRMStatusItem label="Contato" record={crm.contact} />
                  <CRMStatusItem label="Lead" record={crm.lead} />
                </div>

                {crm.lead.exists && crm.lead.owner_name && (
                  <div className="text-xs text-muted-foreground mb-3">
                    Responsável atual: <strong className="text-foreground">{crm.lead.owner_name}</strong>
                  </div>
                )}

                {/* Owner selector + action */}
                <div className="bg-muted/40 -mx-4 -mb-4 px-4 py-3 border-t mt-3">
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">
                    {crm.all_registered ? 'Reatribuir responsável' : 'Responsável pela oportunidade'}
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={ownerId}
                      onChange={(e) => setOwnerId(e.target.value)}
                      className="flex-1 h-10 rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="">Sem responsável</option>
                      {ownerOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <Button onClick={handleSync} disabled={syncing}
                      leftIcon={<UserPlus className="h-4 w-4" />}>
                      {syncing ? 'Processando…'
                        : crm.all_registered ? 'Atualizar responsável' : 'Cadastrar no CRM'}
                    </Button>
                  </div>
                </div>
              </>
            ) : null}
          </section>

          {error && (
            <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">{error}</div>
          )}
          {feedback && (
            <div className="p-3 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-sm">
              {feedback}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-muted/20">
          <a
            href={`https://social.myncc.com.br/assessment/${submission.assessment_slug.replace('-maturity', '')}/resultado/${submission.id}`}
            target="_blank" rel="noopener noreferrer"
            className="text-sm text-emerald-700 hover:underline inline-flex items-center gap-1"
          >
            Ver resultado público <ExternalLink className="h-3.5 w-3.5" />
          </a>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
        </div>
      </div>
    </div>
  )
}

function CRMStatusItem({ label, record }: { label: string; record: CRMRecord }) {
  return (
    <div className={`rounded-md p-3 border text-center ${
      record.exists ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
    }`}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
      <div className={`mt-1.5 inline-flex items-center gap-1 text-xs font-semibold ${
        record.exists ? 'text-emerald-700' : 'text-amber-700'
      }`}>
        {record.exists ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
        {record.exists ? 'Cadastrado' : 'Pendente'}
      </div>
      {record.exists && record.id && (
        <div className="text-[10px] font-mono text-muted-foreground mt-1 truncate" title={record.id}>
          #{record.id.slice(0, 8)}
        </div>
      )}
    </div>
  )
}
